/**
 * AI Intelligence Hero Component - V3
 * Action-packed command center for the dashboard
 * Shows greeting, data status, key metrics, quick actions, and section links
 */
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles, Brain, TrendingUp, ArrowRight, ChevronDown, ChevronUp,
  Lightbulb, Target, Zap, Shield, Activity, AlertTriangle,
  Clock, BarChart3, Users, ListTodo, Layers, PieChart,
  Eye, Star, Building2, MapPin, Wifi, WifiOff
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { getDashboardPatternInsights } from '@/services/patternInsightsApi';
import { INDUSTRY_RECOMMENDATIONS, getIndustryData } from '@/config/industryDemoData';

export const AIIntelligenceHero = ({
  userName,
  businessName,
  businessId,
  industry = 'hospitality',
  metrics = {},
  connectedSources = [],
  lastLogin,
  currentLocation,
  onViewInsights,
  onViewRecommendation,
  onNavigate,
  onSwitchTab,
  showGreeting = true,
  // Corvana AI Synopsis enrichment (passed from the dashboard where the full
  // data set lives) — drives the "since last login" balanced summary.
  kpis = [],
  wins = [],
  completedCount = 0,
  pendingCount = 0,
  highPriorityCount = 0,
  isDemo = false,
  className = ''
}) => {
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    const fetchInsights = async () => {
      if (!businessId || !businessName) { setLoading(false); return; }
      try {
        setLoading(true);
        const data = await getDashboardPatternInsights(businessId, businessName, industry, metrics);
        setInsights(data);
      } catch (err) {
        console.error('Failed to fetch AI insights:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInsights();
  // eslint-disable-next-line
  }, [businessId, businessName, industry]);

  const getTimeGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = userName?.split(' ')[0] || 'there';
  // Demo accounts get the curated industry "Top Actions" showcase. Real accounts
  // never see fabricated recommendation cards (e.g. "+$12K MRR") — empty until
  // live data is wired.
  const recommendations = isDemo ? (getIndustryData(INDUSTRY_RECOMMENDATIONS, industry) || []) : [];

  const getHeadline = () => {
    const rev = metrics?.revenue_change_pct || metrics?.revenue_change || 0;
    const locLabel = currentLocation ? ` at ${currentLocation.shortName}` : '';
    if (rev > 10) return { text: `+${rev.toFixed(0)}% revenue growth${locLabel}`, type: 'success' };
    if (rev > 0) return { text: `Steady +${rev.toFixed(0)}% growth${locLabel}`, type: 'info' };
    if (rev < -5) return { text: `3 opportunities identified${locLabel}`, type: 'warning' };
    return { text: `Your insights are ready${locLabel}`, type: 'neutral' };
  };
  const headline = getHeadline();

  // ── Corvana AI Synopsis ───────────────────────────────────────────────
  // A balanced "since last login" summary: money movement, risks, wins and
  // what the platform surfaced for you. Composed from the data the dashboard
  // already has, so it reflects exactly what's on screen.
  const formatLastLogin = () => {
    if (!lastLogin) return 'your last visit';
    try {
      const d = new Date(lastLogin);
      const diffMs = Date.now() - d.getTime();
      const diffH = Math.floor(diffMs / 3.6e6);
      if (diffH < 1) return 'a little earlier today';
      if (diffH < 24) return `${diffH} hour${diffH > 1 ? 's' : ''} ago`;
      const diffD = Math.floor(diffH / 24);
      if (diffD === 1) return 'yesterday';
      if (diffD < 7) return `${diffD} days ago`;
      return d.toLocaleDateString([], { day: 'numeric', month: 'short' });
    } catch {
      return 'your last visit';
    }
  };

  // Money movement — best KPI delta we can find.
  const moneyKpi = (kpis || []).find(k => typeof (k.change ?? k.trend) === 'number') || (kpis || [])[0] || null;
  const moneyDelta = moneyKpi ? (moneyKpi.change ?? moneyKpi.trend ?? 0) : null;

  // Risk — high-priority work, else the seasonal outlook.
  const riskLine = highPriorityCount > 0
    ? `${highPriorityCount} high-priority item${highPriorityCount > 1 ? 's need' : ' needs'} attention`
    : (insights?.upcoming_trend || 'No urgent risks flagged');

  // Wins — recent positive movement.
  const topWin = (wins || [])[0];
  const winLine = topWin
    ? (topWin.title || topWin.label || topWin.text || topWin.name)
    : (completedCount > 0 ? `${completedCount} task${completedCount > 1 ? 's' : ''} completed` : 'Tracking your wins');

  const synopsisTiles = [
    {
      key: 'money',
      icon: TrendingUp,
      label: 'Money movement',
      value: moneyKpi ? moneyKpi.name : 'Revenue & cash',
      sub: moneyDelta !== null ? `${moneyDelta > 0 ? '+' : ''}${moneyDelta}% vs prior` : 'Connect data for live deltas',
      tone: moneyDelta > 0 ? 'emerald' : moneyDelta < 0 ? 'red' : 'slate',
      tab: 'overview',
    },
    {
      key: 'risk',
      icon: AlertTriangle,
      label: 'Needs attention',
      value: highPriorityCount > 0 ? `${highPriorityCount} priority` : 'All clear',
      sub: riskLine,
      tone: highPriorityCount > 0 ? 'amber' : 'slate',
      tab: 'tasks',
    },
    {
      key: 'wins',
      icon: Star,
      label: 'Wins & progress',
      value: (wins || []).length > 0 ? `${wins.length} new` : `${completedCount} done`,
      sub: winLine,
      tone: 'indigo',
      tab: 'overview',
    },
  ];

  const TONE = {
    emerald: 'text-emerald-600 bg-emerald-50 border-emerald-100',
    red: 'text-red-600 bg-red-50 border-red-100',
    amber: 'text-amber-600 bg-amber-50 border-amber-100',
    indigo: 'text-indigo-600 bg-indigo-50 border-indigo-100',
    slate: 'text-slate-600 bg-slate-50 border-slate-100',
  };

  // Robustly switch the parent <Tabs>. Prefer the React state setter passed
  // from the dashboard; fall back to clicking the Radix trigger by its id
  // suffix (Radix does NOT emit a `value` HTML attribute on the DOM).
  const switchTab = (tab) => {
    if (typeof onSwitchTab === 'function') {
      onSwitchTab(tab);
      return;
    }
    const trigger = document.querySelector(`[role="tab"][id$="-trigger-${tab}"]`);
    if (trigger) trigger.click();
  };

  // Key stats from insights
  const keyMetrics = insights?.key_insights?.slice(0, 3) || [];

  if (loading) {
    return (
      <div className={`rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200/60 p-6 ${className}`} data-testid="ai-intelligence-hero">
        <div className="flex items-center gap-3">
          <motion.div animate={{ rotate: 360 }} transition={{ duration: 2, repeat: Infinity, ease: "linear" }}>
            <Brain className="w-8 h-8 text-indigo-500" />
          </motion.div>
          <div>
            <p className="font-semibold text-slate-700">Analyzing {businessName}...</p>
            <p className="text-xs text-slate-400">Preparing your intelligence briefing</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-2xl border border-slate-200/60 bg-white ${className}`}
      data-testid="ai-intelligence-hero"
    >
      {/* Subtle background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-indigo-50/40 via-transparent to-purple-50/20 pointer-events-none" />

      <div className="relative">
        {/* Top bar: Greeting + Data Status — hidden when the unified hero
            already renders the greeting (iter-390 de-duplication). */}
        {showGreeting && (
        <div className="px-5 pt-5 pb-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-md shadow-indigo-500/20">
              <Brain className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-slate-900 tracking-tight">
                {getTimeGreeting()}, {firstName}
              </h2>
              {currentLocation && (
                <p className="text-xs font-medium text-indigo-600 mt-0.5 flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Viewing: {businessName}
                </p>
              )}
              <div className="flex items-center gap-2 mt-0.5">
                {connectedSources && connectedSources.length > 0 ? (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                      </span>
                      <span className="text-xs font-medium text-emerald-600">DATA LIVE</span>
                    </div>
                    <span className="text-slate-300">|</span>
                    <span className="text-xs text-slate-400 flex items-center gap-1">
                      <Shield className="w-3 h-3 text-emerald-500" />
                      ATO + ABS + RBA verified
                    </span>
                    <span className="text-slate-300 hidden sm:inline">|</span>
                  </>
                ) : (
                  <>
                    <div className="flex items-center gap-1.5">
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-300"></span>
                      <span className="text-xs font-medium text-slate-500">No data source connected</span>
                    </div>
                    <span className="text-slate-300 hidden sm:inline">|</span>
                  </>
                )}
                <span className="text-xs text-slate-400 hidden sm:flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
          </div>

          {/* Headline badge */}
          <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium self-start sm:self-auto
            ${headline.type === 'success' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :
              headline.type === 'warning' ? 'bg-amber-50 text-amber-700 border border-amber-200' :
              headline.type === 'info' ? 'bg-blue-50 text-blue-700 border border-blue-200' :
              'bg-slate-50 text-slate-600 border border-slate-200'}`}
          >
            {headline.type === 'success' && <TrendingUp className="w-3.5 h-3.5" />}
            {headline.type === 'warning' && <AlertTriangle className="w-3.5 h-3.5" />}
            {headline.type === 'info' && <Activity className="w-3.5 h-3.5" />}
            {headline.type === 'neutral' && <Sparkles className="w-3.5 h-3.5" />}
            {headline.text}
          </div>
        </div>
        )}

        {/* Corvana AI Synopsis — replaces the old duplicate nav pills.
            A balanced "since last login" summary of what's on the dashboard. */}
        <div className="px-5 pb-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg shadow-sm">
              <Brain className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-semibold text-slate-900 leading-tight">Corvana AI Synopsis</p>
              <p className="text-[11px] text-slate-500 leading-tight">
                What's changed since {formatLastLogin()}
              </p>
            </div>
            {insights?.pattern_confidence && (
              <Badge className="ml-auto bg-indigo-50 text-indigo-600 border-indigo-200 text-[9px] flex-shrink-0">
                {insights.pattern_confidence} confidence
              </Badge>
            )}
          </div>

          {/* AI narrative line */}
          {(insights?.ai_generated_insight || insights?.greeting_insight) && (
            <p
              className="text-xs text-slate-600 leading-relaxed mb-3 line-clamp-3"
              data-testid="ai-synopsis-narrative"
            >
              {insights.ai_generated_insight || insights.greeting_insight}
            </p>
          )}

          {/* Balanced synopsis tiles: money / risk / wins */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {synopsisTiles.map((tile) => {
              const Icon = tile.icon;
              return (
                <button
                  key={tile.key}
                  onClick={() => switchTab(tile.tab)}
                  className="text-left p-3 rounded-xl bg-white border border-slate-100 hover:border-indigo-200 hover:shadow-sm transition-all"
                  data-testid={`ai-synopsis-${tile.key}`}
                >
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className={`p-1 rounded-md border ${TONE[tile.tone]}`}>
                      <Icon className="w-3 h-3" />
                    </span>
                    <span className="text-[10px] uppercase tracking-wider text-slate-400 font-medium truncate">
                      {tile.label}
                    </span>
                  </div>
                  <p className="text-sm font-bold text-slate-900 truncate">{tile.value}</p>
                  <p className="text-[10px] text-slate-500 truncate">{tile.sub}</p>
                </button>
              );
            })}
          </div>
        </div>

        {/* Upcoming AI Prediction */}
        {insights?.upcoming_trend && (
          <div className="mx-5 mb-3 p-3 bg-gradient-to-r from-indigo-50/80 to-purple-50/60 rounded-xl border border-indigo-100/80 flex items-center gap-3">
            <div className="p-1.5 bg-indigo-100 rounded-lg flex-shrink-0">
              <Target className="w-3.5 h-3.5 text-indigo-600" />
            </div>
            <p className="text-xs font-medium text-indigo-800 flex-1">{insights.upcoming_trend}</p>
            <Badge className="bg-indigo-100/80 text-indigo-700 border-indigo-200 text-[9px] flex-shrink-0">
              AI Prediction
            </Badge>
          </div>
        )}

        {/* Key Metrics Row (from API insights) */}
        {keyMetrics.length > 0 && (
 <div className="px-5 pb-3 grid grid-cols-1 gap-2">
            {keyMetrics.map((metric, idx) => (
              <div
                key={`${metric?.id ?? metric?.name ?? metric?.title ?? 'metric'}-${idx}`}
                className="p-3 rounded-xl bg-slate-50/80 border border-slate-100 hover:bg-white hover:border-slate-200 transition-all cursor-pointer"
                onClick={() => onViewInsights?.(metric)}
              >
                <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-1">{metric.metric_name}</p>
                <div className="flex items-center gap-1.5">
                  <span className="text-base font-bold text-slate-900">
                    {metric.change_percent > 0 ? '+' : ''}{metric.change_percent}%
                  </span>
                  <TrendingUp className={`w-3 h-3 ${metric.change_direction === 'up' ? 'text-emerald-500' : 'text-red-500 rotate-180'}`} />
                </div>
              </div>
            ))}
          </div>
        )}

        {/* AI Recommendations - Compact Row (curated showcase; demo only) */}
        {recommendations.length > 0 && (
        <div className="px-5 pb-4 border-t border-slate-100/80 pt-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-2 w-full text-left mb-2"
          >
            <Lightbulb className="w-4 h-4 text-amber-500" />
            <span className="text-xs font-semibold text-slate-700">Top Actions</span>
            <Badge className="bg-amber-50 text-amber-600 border-amber-200 text-[9px]">{recommendations.length}</Badge>
            <ArrowRight className={`w-3 h-3 text-slate-400 ml-auto transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </button>

          <AnimatePresence>
            {expanded ? (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-2 overflow-hidden"
              >
                {recommendations.map((rec, idx) => (
                  <div
                    key={`${rec?.id ?? rec?.name ?? rec?.title ?? 'rec'}-${idx}`}
                    className="flex items-center gap-3 p-2.5 rounded-lg bg-slate-50 border border-slate-100 hover:bg-white hover:border-indigo-200 transition-all cursor-pointer"
                    onClick={() => onViewRecommendation?.(rec)}
                  >
                    <div className={`w-1.5 h-8 rounded-full flex-shrink-0 ${
                      rec.priority === 'high' ? 'bg-red-400' : rec.priority === 'medium' ? 'bg-amber-400' : 'bg-blue-400'
                    }`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-slate-800 truncate">{rec.title}</p>
                      <p className="text-[10px] text-slate-500">{rec.source} &middot; {rec.confidence}% confidence</p>
                    </div>
                    <Badge className="bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px] flex-shrink-0">{rec.impact}</Badge>
                  </div>
                ))}
              </motion.div>
            ) : (
              <div className="flex gap-2">
                {recommendations.slice(0, 3).map((rec, idx) => (
                  <motion.div
                    key={`${rec?.id ?? rec?.name ?? rec?.title ?? 'rec'}-${idx}`}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex-1 p-2.5 bg-slate-50/80 rounded-lg border border-slate-100 cursor-pointer hover:bg-white hover:border-indigo-200 transition-all"
                    onClick={() => onViewRecommendation?.(rec)}
                  >
                    <div className="flex items-center gap-1.5 mb-1">
                      <div className={`w-1 h-1 rounded-full ${
                        rec.priority === 'high' ? 'bg-red-400' : rec.priority === 'medium' ? 'bg-amber-400' : 'bg-blue-400'
                      }`} />
                      <span className="text-[10px] font-medium text-slate-700 truncate">{rec.title}</span>
                    </div>
                    <span className="text-[9px] text-emerald-600 font-medium">{rec.impact}</span>
                  </motion.div>
                ))}
              </div>
            )}
          </AnimatePresence>
        </div>
        )}
      </div>
    </motion.div>
  );
};

export default AIIntelligenceHero;
